import { Component, OnDestroy, OnInit } from '@angular/core';
import { distinctUntilChanged, Observable, Subscription, take, tap } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { FormControl } from '@angular/forms';
import { DEFAULT_PAGE_SIZE, PAGE_SIZE_OPTIONS } from 'src/app/config/global.config';
import { IPageCount, IPagination } from 'src/app/shared/models/page.model';
import { ICategory } from '../../models/categories.models';
import { MatDialog } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { AppState } from 'src/app/core/app.state';
import { AddCategorySuccess, DeleteCategory, DeleteCategorySuccess, FetchCategories, UpdateCategorySuccess } from '../../store/categories.actions';
import { AddCategoriesComponent } from '../add-categories/add-categories.component';
import { ConfirmationPopUpComponent } from 'src/app/shared/confirmation-pop-up/confirmation-pop-up.component';
import { environment } from 'src/environments/environment';
import { Actions, ofType } from '@ngrx/effects';
import { TranslateService } from '@ngx-translate/core';
import { ActivatedRoute, Router } from '@angular/router';
import { SelectionController } from 'src/app/shared/utils/selection-controller';

@Component({
  selector: 'app-list-categories',
  templateUrl: './list-categories.component.html',
  styleUrls: ['./list-categories.component.scss']
})
export class ListCategoriesComponent implements OnInit, OnDestroy {
  page: IPageCount = {
    size: DEFAULT_PAGE_SIZE,
    number: 1
  }
  categories$: Observable<ICategory[]> = this.store.select((state) => state.categories.categories);
  pagination$: Observable<IPagination | null> = this.store.select((state) => state.categories.metadata);
  key: string | undefined = undefined;
  isAppLoading$ = this.store.select((state) => state.shared.isAppLoading);
  baseUrl = environment.BASE_URL_API;
  timestamp = new Date().getTime();

  searchControl = new FormControl('');

  /** Row selection, range gestures and the tri-state header, in one object. */
  readonly selection = new SelectionController<ICategory>((item) => item.id);

  subscriptions: Subscription[] = [];

  constructor(
    private store: Store<AppState>,
    public dialog: MatDialog,
    private actions$: Actions,
    private translateService: TranslateService,
    private router: Router,
    private activatedRoute: ActivatedRoute
  ) {
    this.activatedRoute.queryParams.subscribe(params => {
      const pageNumber = parseInt(params['page'] || '1');
      const pageCount = PAGE_SIZE_OPTIONS.includes(parseInt(params['count'])) ? parseInt(params['count']) : DEFAULT_PAGE_SIZE;
      this.key = params['keyword'] ?? '';
      this.page = { ...this.page, number: pageNumber, size: pageCount };
    });
  }

  ngOnInit(): void {
    this.searchControl.setValue(this.key || '', { emitEvent: false });

    const sub1 = this.store.select('categories', 'keyword')
      .pipe(
        distinctUntilChanged(),
        tap(keyword => {
          this.key = keyword;
          this.toPage(this.page);
        })
      )
      .subscribe();

    const sub2 = this.actions$.pipe(ofType(AddCategorySuccess, UpdateCategorySuccess)).subscribe(() => {
      this.timestamp = new Date().getTime();
    });

    const sub3 = this.searchControl.valueChanges
      .pipe(debounceTime(300), distinctUntilChanged())
      .subscribe(value => {
        const next = (value ?? '').toString();
        if (next === (this.key ?? '')) return;
        this.key = next;
        this.page = { ...this.page, number: 1 };
        this.toPage(this.page);
      });

    // Deferred so the selection state settles outside the change-detection pass
    // that delivered the new rows, which would otherwise trip ExpressionChanged.
    const sub4 = this.categories$.subscribe(list => {
      Promise.resolve().then(() => this.selection.setRows(list));
    });

    this.subscriptions.push(sub1, sub2, sub3, sub4);
  }

  getImage(image: string): string {
    return this.baseUrl + image + '?thumbnail=1&timestamp=' + this.timestamp;
  }

  toPage(page: IPageCount) {
    this.page = { ...this.page, number: page.number, size: page.size };
    this.store.dispatch(FetchCategories({ page: this.page, key: this.key }));
    const filter: any = {};
    if (this.key && this.key !== '') {
      filter.keyword = this.key;
    }
    const queryParams = { page: this.page.number, count: this.page.size, ...filter };
    this.router.navigate([], {
      queryParams: queryParams
    });
  }

  openCategorie(categorie?: ICategory) {
    this.dialog.open(AddCategoriesComponent, {
      data: categorie,
      disableClose: true,
    });
  }

  // ── Selection ────────────────────────────────────────────────────────────

  bulkDelete(): void {
    if (!this.selection.hasSelection) return;
    const dialogRef = this.dialog.open(ConfirmationPopUpComponent, {
      data: {
        icon: '../../../assets/icones/cancel_alert.svg',
        confirm_text: this.translateService.instant('common.remove'),
      },
    });
    dialogRef.afterClosed().subscribe((result: boolean) => {
      if (!result) return;
      const ids = this.selection.ids;
      this.selection.clear();
      const refreshSub = this.actions$.pipe(
        ofType(DeleteCategorySuccess),
        take(ids.length)
      ).subscribe(() => {
        refreshSub.unsubscribe();
        this.toPage(this.page);
      });
      ids.forEach(id => this.store.dispatch(DeleteCategory({ id })));
    });
  }

  delete(categorie: ICategory) {
    const dialogRef = this.dialog.open(ConfirmationPopUpComponent, {
      data: {
        icon: '../../../assets/icones/cancel_alert.svg',
        confirm_text: this.translateService.instant('common.remove'),
      },
    });
    dialogRef.afterClosed().subscribe((result: boolean) => {
      if (result) {
        const deleteSuccessSub = this.actions$.pipe(
          ofType(DeleteCategorySuccess),
          take(1)
        ).subscribe(() => {
          this.toPage(this.page);
          deleteSuccessSub.unsubscribe();
        });

        this.store.dispatch(DeleteCategory({ id: categorie.id }));
      }
    });
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub => sub.unsubscribe()));
  }

}
